import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbExternalLink, TbMessageCircle } from "solid-icons/tb";
import { createMemo, For, Show } from "solid-js";
import { safeParseBlockJson } from "../../message/parseBlockContent";
import { useChatRuntime } from "../../runtime/ChatRuntime";
import { formatIsoTimestamp } from "../../util/dateFormat";
import { type NotionCommentsPayload, parseNotionCommentsPayload } from "./notionCommentsSchema";

interface NotionCommentsRendererProps {
  block: StreamingBlock;
}

// The page URL arrives from tool output and is untrusted; only https URLs are
// safe to put in an href.
const safeNotionUrl = (url: string | undefined): string | undefined => (url?.startsWith("https://") ? url : undefined);

// Authors render as bare Notion user ids; the backend does not resolve names.
export const NotionCommentsRenderer = (props: NotionCommentsRendererProps) => {
  const rt = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<NotionCommentsPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseNotionCommentsPayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {rt.t("notion_comments_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          // An agent fans these out several per turn, so a miss draws nothing.
          <Show when={data().discussions.some((discussion) => discussion.comments.length > 0)}>
            <div class="overflow-hidden rounded-lg border border-primary-border bg-panel">
              <div class="flex items-center gap-2 border-b border-primary-border px-3 py-2">
                <TbMessageCircle class="size-4 shrink-0 text-brand" />
                <span class="text-sm font-medium text-primary-text">{rt.t("notion_comments_card.title")}</span>
                <span class="ml-auto text-xs text-muted-text">
                  {data().discussions.length === 1
                    ? rt.t("notion_comments_card.discussions_one")
                    : rt.t("notion_comments_card.discussions", { count: String(data().discussions.length) })}
                </span>
                <Show when={safeNotionUrl(data().pageUrl)}>
                  {(href) => (
                    <a
                      href={href()}
                      target="_blank"
                      rel="noopener noreferrer"
                      class="inline-flex shrink-0 items-center gap-1 text-xs text-brand hover:underline"
                    >
                      {rt.t("notion_comments_card.open_link")}
                      <TbExternalLink class="size-3.5 shrink-0" aria-hidden="true" />
                    </a>
                  )}
                </Show>
              </div>

              <ul class="divide-y divide-primary-border">
                <For each={data().discussions}>
                  {(discussion) => (
                    <li class="px-3 py-2">
                      <ul class="space-y-2">
                        <For each={discussion.comments}>
                          {(comment, index) => (
                            <li classList={{ "ps-4": index() > 0 }}>
                              <div class="flex items-baseline justify-between gap-3">
                                <span class="truncate font-mono text-xs text-muted-text">{comment.authorId}</span>
                                <Show when={comment.createdTime}>
                                  <span class="shrink-0 text-xs text-muted-text">
                                    {formatIsoTimestamp(comment.createdTime)}
                                  </span>
                                </Show>
                              </div>
                              <p class="line-clamp-3 text-sm wrap-break-word whitespace-pre-wrap text-primary-text">
                                {comment.text}
                              </p>
                            </li>
                          )}
                        </For>
                      </ul>
                    </li>
                  )}
                </For>
              </ul>
            </div>
          </Show>
        )}
      </Show>
    </Show>
  );
};
